import { ethers } from 'ethers';

// Synth category and name live here: the CDP only stores tickers
export const CATEGORIES = ['Specials', 'Indexes', 'Crypto', 'Commodities', 'Forex', 'Equities'];
const SYNTHS = {
  ciBOLD: ['Specials', 'Bitcoin + Gold'],
  ciUSD: ['Specials', 'US Dollar'],
  ciSDR: ['Indexes', 'IMF SDR basket'],
  ciPRIV: ['Indexes', 'Privacy coins'],
  ciMUSK: ['Indexes', 'Musk basket'],
  ciDEFI12: ['Indexes', 'DeFi 12'],
  ciDPI: ['Indexes', 'DeFi Pulse'],
  ciDASH: ['Crypto', 'Dash'],
  ciLTC: ['Crypto', 'Litecoin'],
  ciXMR: ['Crypto', 'Monero'],
  ciZCASH: ['Crypto', 'Zcash'],
  ciXAU: ['Commodities', 'Gold'],
  ciXAG: ['Commodities', 'Silver'],
  ciXPT: ['Commodities', 'Platinum'],
  ciGBP: ['Forex', 'British Pound'],
  ciYEN: ['Forex', 'Japanese Yen'],
  ciBRL: ['Forex', 'Brazilian Real'],
  ciAAPL: ['Equities', 'Apple'],
  ciAMZN: ['Equities', 'Amazon'],
  ciGOOGL: ['Equities', 'Alphabet'],
  ciMSFT: ['Equities', 'Microsoft'],
  ciTSLA: ['Equities', 'Tesla'],
};
export const SYNTH_CATEGORY = Object.fromEntries(Object.entries(SYNTHS).map(([ticker, [category]]) => [ticker, category]));
export const categoryOf = (ticker) => SYNTH_CATEGORY[ticker] || 'Specials';
export const synthName = (ticker) => (SYNTHS[ticker] ? SYNTHS[ticker][1] : '');

export const getTokenIcon = (ticker) => {
  try {
    return require(`../../assets/unisynths/${ticker}.png`);
  } catch (err) {
    return null;
  }
};

/** BigNumber -> float (UI math only, never for amounts sent on-chain) */
export const num = (value, decimals = 18) => (value ? parseFloat(ethers.utils.formatUnits(value, decimals)) : 0);

/** User input -> BigNumber with `decimals`, extra decimals dropped; null when not a positive number */
export const toUnits = (input, decimals = 18) => {
  if (input === undefined || input === null) return null;
  const [whole, frac = ''] = String(input).trim().split('.');
  if (!/^\d*$/.test(whole) || !/^\d*$/.test(frac) || (!whole && !frac)) return null;
  const value = ethers.utils.parseUnits(`${whole || '0'}.${frac.slice(0, decimals) || '0'}`, decimals);
  return value.gt(0) ? value : null;
};

/** Float -> input string, rounded down so MAX buttons never exceed the real limit */
export const toInput = (value, decimals = 6) => {
  if (!value || value <= 0 || !Number.isFinite(value)) return '';
  const factor = 10 ** Math.min(decimals, 8);
  return (Math.floor(value * factor) / factor).toString();
};

export const formatUsd = (value) =>
  value === null || value === undefined || !Number.isFinite(value)
    ? 'N/A'
    : new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: value !== 0 && Math.abs(value) < 1 ? 5 : 2,
      }).format(value);

export const formatAmount = (value, digits = 4) =>
  Number.isFinite(value) ? value.toLocaleString('en-US', { maximumFractionDigits: digits }) : 'N/A';

export const formatPct = (bps) => `${(bps / 100).toFixed(bps % 100 ? 1 : 0)}%`;

/** collateral USD / debt USD in bps; Infinity without debt */
export const ratioBps = (collateralUsd, debtUsd) => (debtUsd > 0 ? (collateralUsd / debtUsd) * 10000 : Infinity);

/** Tailwind color for a collateral ratio against the collateral's thresholds */
export const ratioTone = (bps, mcrBps, liquidationRatioBps) => {
  if (bps === Infinity) return 'text-white/70';
  if (bps < liquidationRatioBps) return 'text-rose-400';
  if (bps < mcrBps) return 'text-amber-300';
  return 'text-emerald-400';
};

export const formatRatio = (bps) => (bps === Infinity ? '∞' : `${(bps / 100).toFixed(1)}%`);

/** Synth price at which the position reaches the liquidation ratio */
export const liquidationPrice = (collateralUsd, debtUnits, liquidationRatioBps) =>
  debtUnits > 0 ? (collateralUsd * 10000) / liquidationRatioBps / debtUnits : null;

export const sameAddress = (a, b) => !!a && !!b && a.toLowerCase() === b.toLowerCase();

/** Floats for a position, priced with the market snapshot (prices are null while an oracle is unavailable) */
export const describePosition = (position, market) => {
  const synth = market.synths.find((s) => sameAddress(s.id, position.synth));
  const collateral = market.collaterals.find((c) => sameAddress(c.address, position.collateral));
  const collateralAmount = num(position.collateralAmount, collateral.decimals);
  const collateralPrice = collateral.price ? num(collateral.price) : null;
  const collateralUsd = collateralPrice !== null ? collateralAmount * collateralPrice : null;
  const debt = num(position.debt);
  const price = synth.price ? num(synth.price) : null;
  const debtUsd = price !== null ? debt * price : null;

  let ratio = null;
  if (position.ratioBps) {
    ratio = position.ratioBps.gt(10 ** 12) ? Infinity : position.ratioBps.toNumber();
  } else if (collateralUsd !== null && debtUsd !== null) {
    ratio = ratioBps(collateralUsd, debtUsd);
  }

  return {
    synth,
    collateral,
    collateralAmount,
    collateralPrice,
    collateralUsd,
    debt,
    price,
    debtUsd,
    ratio,
    liquidationPrice: collateralUsd !== null ? liquidationPrice(collateralUsd, debt, collateral.liquidationRatioBps) : null,
  };
};
